import React, { useEffect, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import BlogPostTable, { COLUMNS_CONSTANTS as BLOG_POST_COLUMNS } from "./blog_post/BlogPostTable";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import NotesDrawer from "bundles/Internal/components/common/NotesDrawer";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import CreateBlogPostModal from "bundles/Internal/components/blog_post/CreateBlogPostModal";
import exportCsv from "bundles/common/utils/exportCsv";
import {
  EMPTY_BLOG_POST_FILTERS,
} from "bundles/Internal/components/blog_post/BlogPostTableFilters";

function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.preferred_locale?.length) {
    filters.preferred_locale = activeFilters.preferred_locale;
    hasAny = true;
  }

  if (activeFilters.category?.length) {
    filters.category = activeFilters.category;
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const BlogPostsIndexPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [blogPosts, setBlogPosts] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const currentInternalUser = useSelector((state) => state.current_internal_user);
  const canManage = ["coordinator", "admin"].includes(currentInternalUser?.role);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesBlogPost, setNotesBlogPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_BLOG_POST_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteBlogPost, setDeleteBlogPost] = useState(null);

  const fetchBlogPosts = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/blog_posts", {
      params: {
        q: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setBlogPosts(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch blog posts", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchBlogPosts(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchBlogPosts, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchBlogPosts(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchBlogPosts, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleExportCsv = useCallback((ids) => {
    exportCsv("blog_posts", ids, authenticity_token, BLOG_POST_COLUMNS)
      .then(() => enqueueSnackbar("CSV exported", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to export CSV", { variant: "error" }));
  }, [authenticity_token, enqueueSnackbar]);

  const handleCreateBlogPost = useCallback((formData, { reset }) => {
    setSubmitting(true);
    sh.post("/internal/blog_posts", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((res) => {
        enqueueSnackbar("Blog post created", { variant: "success" });
        reset();
        setCreateModalOpen(false);
        if (res.data && res.data.id) {
          setBlogPosts((prev) => [res.data, ...prev]);
          setTotalCount((prev) => prev + 1);
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          enqueueSnackbar("Only coordinators and admins can create blog posts", { variant: "error" });
          return;
        }
        const messages = err?.response?.data?.errors;
        enqueueSnackbar(
          messages ? messages.join(", ") : "Failed to create blog post",
          { variant: "error" }
        );
      })
      .finally(() => setSubmitting(false));
  }, [enqueueSnackbar]);

  const handleEditClick = useCallback((blogPost) => {
    sh.get(`/internal/blog_posts/${blogPost.id}`)
      .then((res) => {
        setEditingBlogPost(res.data);
        setCreateModalOpen(true);
      })
      .catch(() => {
        enqueueSnackbar("Failed to load blog post", { variant: "error" });
      });
  }, [enqueueSnackbar]);

  const handleUpdateBlogPost = useCallback((formData, { reset }) => {
    if (!editingBlogPost?.id) return;
    setSubmitting(true);
    sh.patch(`/internal/blog_posts/${editingBlogPost.id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then(() => {
        enqueueSnackbar("Blog post updated", { variant: "success" });
        reset();
        setCreateModalOpen(false);
        setEditingBlogPost(null);
        fetchBlogPosts(searchQuery, page, rowsPerPage, filters);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          enqueueSnackbar("Only coordinators and admins can edit blog posts", { variant: "error" });
          return;
        }
        const messages = err?.response?.data?.errors;
        enqueueSnackbar(
          messages ? messages.join(", ") : "Failed to update blog post",
          { variant: "error" }
        );
      })
      .finally(() => setSubmitting(false));
  }, [editingBlogPost, enqueueSnackbar, fetchBlogPosts, searchQuery, page, rowsPerPage, filters]);

  const handleDeleteBlogPost = useCallback(() => {
    if (!deleteBlogPost) return;
    sh.delete(`/internal/blog_posts/${deleteBlogPost.id}`)
      .then(() => {
        setDeleteBlogPost(null);
        enqueueSnackbar("Blog post deleted", { variant: "success" });
        fetchBlogPosts(searchQuery, page, rowsPerPage, filters);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          enqueueSnackbar("Only coordinators and admins can delete blog posts", { variant: "error" });
          return;
        }
        const msg = err?.response?.data?.errors?.[0] || "Failed to delete blog post";
        enqueueSnackbar(msg, { variant: "error" });
      });
  }, [deleteBlogPost, enqueueSnackbar, fetchBlogPosts, searchQuery, page, rowsPerPage, filters]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <BlogPostTable
        blogPosts={blogPosts}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        onBlogPostSelect={(blogPost) => {
          if (!blogPost?.id) return;
          navigate(`/internal/blog_posts/${blogPost.id}`, {
            state: { blogPost, from: location.pathname },
          });
        }}
        onOpenNotes={(blogPost) => {
          setNotesBlogPost(blogPost);
          setNotesOpen(true);
        }}
        onEdit={handleEditClick}
        onDelete={(blogPost) => setDeleteBlogPost(blogPost)}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectable
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        onExportCsv={handleExportCsv}
        onAddClick={() => {
          setEditingBlogPost(null);
          setCreateModalOpen(true);
        }}
        addButtonLabel="New blog post"
        canManage={canManage}
      />
      <NotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        resource="blog_posts"
        record={notesBlogPost}
        onNoteAdded={(blogPostId) => {
          setBlogPosts((prev) =>
            prev.map((bp) =>
              String(bp?.id) === String(blogPostId)
                ? { ...bp, notes_count: (Number(bp.notes_count) || 0) + 1 }
                : bp
            )
          );
        }}
        onNoteArchived={(blogPostId) => {
          setBlogPosts((prev) =>
            prev.map((bp) =>
              String(bp?.id) === String(blogPostId)
                ? { ...bp, notes_count: Math.max((Number(bp.notes_count) || 0) - 1, 0) }
                : bp
            )
          );
        }}
      />
      <CreateBlogPostModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditingBlogPost(null);
        }}
        onSubmit={editingBlogPost ? handleUpdateBlogPost : handleCreateBlogPost}
        submitting={submitting}
        blogPost={editingBlogPost}
      />
      <ConfirmDialog
        open={Boolean(deleteBlogPost)}
        onClose={() => setDeleteBlogPost(null)}
        onConfirm={handleDeleteBlogPost}
        title={`Delete Blog Post #${deleteBlogPost?.id}?`}
        description="This action cannot be undone. The blog post will be permanently removed."
        confirmLabel="Delete"
        destructive
      />
    </Box>
  );
};

export default BlogPostsIndexPage;
